import React, { useState, useEffect } from 'react';
import { Card, Spinner, TextField } from 'ui-component-library';
import { func } from 'prop-types';

import InfoSection from '../../common/card-shared-components/card-info-section/card-info-section';
import LabelValue from '../../common/card-shared-components/card-label-value/card-label-value';
import InfoTooltipButton from '../../common/info-tooltip-button/info-tooltip-button';
import { showErrorMessage, showSuccessMessage } from '../../../../utils/common';
import { updateIdentifierInformation, updateInterlineCarrierInformation } from '../../../../utils/api/load';
import ImageIcon from '../../common/image-icon';
import lockShape from '../../../../shapes/lock';
import loadShape from '../../../../shapes/load';
import editIcon from '../../../../assets/icon-pencil.svg';
import { HeaderButtonsGroup, HeaderButton, LoadingContainer } from './load-identifiers-card.style';

import { MOCK_INTERLINE_DETAILS } from './mocks';
import { IDENTIFIER_DEF } from './identifier-def';

/**
 * Load identifier info component
 * @param {object} params - Object containing props
 * @param {object} params.load - Load object
 * @param {object} params.lock - The lock (if present) on the load
 * @param {func} params.refetchLoad - Function to refetch the load
 * @return {element} - Driver Truck Info component
 */
const LoadIdentifiersCard = ({ lock, load, refetchLoad }) => {
  // Component state hooks
  const [editingIdentifierInfo, setEditingIdentifierInfo] = useState(false);
  const [editingInterlineCarrier, setEditingInterlineCarrier] = useState(false);
  const [identifierInfoLoading, setIdentifierInfoLoading] = useState(false);
  const [interlineLoading, setInterlineLoading] = useState(false);
  const [identifiers, setIdentifiers] = useState(null);
  const [updatedIdentifiers, setUpdatedIdentifiers] = useState({});
  const [interlineDetails, setInterlineDetails] = useState(MOCK_INTERLINE_DETAILS);

  useEffect(() => {
    if (load?.referenceIdentifiers) {
      const ids = IDENTIFIER_DEF.map((identifier) => ({
        key: identifier.key,
        value: load?.referenceIdentifiers[identifier.key],
        label: identifier.label,
      }));

      setIdentifiers(ids);
    }
  }, [load]);

  /**
   * Handles when a user updates identifier information.
   * @return {void}
   */
  // TODO: Update once BE service is available. TT-2021
  const onUpdateIdentifierInfo = () => {
    const payload = {
      ...updatedIdentifiers,
      salesOrderBk: load.billOfLadingNumber,
    };
    setIdentifierInfoLoading(true);
    updateIdentifierInformation(payload).then(() => {
      showSuccessMessage(`Identifier information has been updated for load #${load.billOfLadingNumber}. It may take a moment to update.`);
      refetchLoad({});
    }).catch(() => {
      showErrorMessage(`Could not update identifier information for load #${load.billOfLadingNumber}`);
    }).finally(() => {
      setIdentifierInfoLoading(false);
    });
    setEditingIdentifierInfo(false);
  };

  /**
   * Handles when a user updates interline carrier.
   * @return {void}
   */
  // TODO: Update once BE service is available. TT-2021
  const onUpdateInterlineCarrier = () => {
    const payload = {};
    setInterlineLoading(true);
    updateInterlineCarrierInformation(payload).then(() => {
      showSuccessMessage(`Interline Carrier information has been updated for load #${load.billOfLadingNumber}. It may take a moment to update.`);
      refetchLoad({});
    }).catch(() => {
      showErrorMessage(`Could not update Interline Carrier information for load #${load.billOfLadingNumber}`);
    }).finally(() => {
      setInterlineLoading(false);
    });
    setEditingInterlineCarrier(false);
  };

  /**
   * Renders identifier information header content
   * @return {element}
   */
  const renderIdentifierInfoHeaderContent = () => {
    if (lock?.loadId && !lock?.ownedByUser) {
      return null;
    }
    return (!editingIdentifierInfo ? (
      <ImageIcon
        height={15}
        width={15}
        src={editIcon}
        onClick={() => setEditingIdentifierInfo(true)}
        altString="Edit Icon"
        offCenter
      />
    ) : (
      <HeaderButtonsGroup>
        <HeaderButton onClick={() => setEditingIdentifierInfo(false)} type="cancel" text="Cancel" />
        <HeaderButton onClick={onUpdateIdentifierInfo} type="submit" text="Save" />
      </HeaderButtonsGroup>
    ));
  };

  /**
   * Renders Interline Carrier header content
   * @return {element}
   */
  const renderInterlineCarrierHeaderContent = () => {
    if (lock?.loadId && !lock?.ownedByUser) {
      return null;
    }
    return (!editingInterlineCarrier ? (
      <ImageIcon
        height={15}
        width={15}
        src={editIcon}
        onClick={() => setEditingInterlineCarrier(true)}
        altString="Edit Icon"
        offCenter
      />
    ) : (
      <HeaderButtonsGroup>
        <HeaderButton onClick={() => setEditingInterlineCarrier(false)} type="cancel" text="Cancel" />
        <HeaderButton onClick={onUpdateInterlineCarrier} type="submit" text="Save" />
      </HeaderButtonsGroup>
    ));
  };

  /**
   * Edit Identifier control
   * @return {element}
   */
  const editIdentifierField = (identifier, index) => (
    <TextField
      name={identifier.label}
      value={identifier.value}
      onValueChange={(event) => {
        const identifiersCopy = [...identifiers];
        identifiersCopy[index].value = event.target.value;
        setIdentifiers(identifiersCopy);
        const updatedIdentifier = {};
        updatedIdentifiers[identifier.key] = event.target.value;
        setUpdatedIdentifiers({
          ...updatedIdentifiers,
          ...updatedIdentifier,
        });
      }}
    />
  );

  /**
   * Edit Interline control
   * @return {element}
   */
  const editInterlineField = (item, index) => (
    <TextField
      name={item.label}
      value={item.value}
      onValueChange={(event) => {
        const itemsCopy = [...interlineDetails];
        itemsCopy[index].value = event.target.value;
        setInterlineDetails(itemsCopy);
      }}
    />
  );

  if (!identifiers) {
    return <></>;
  }

  return (
    <Card
      title="Other Load Identifiers &amp; Interline Carrier Details"
      headerContent={<InfoTooltipButton text="" isPrimary />}
      isPaddingRemoved
    >
      <InfoSection numCols={3} rowGap={8} header="Load Identifiers" headerContent={renderIdentifierInfoHeaderContent()}>
        {!identifierInfoLoading && (
          <>
            {identifiers.map((identifier, index) => (
              <LabelValue
                label={identifier.label}
                value={load.referenceIdentifiers[identifier.key]}
                options={{ stacked: true }}
                editOptions={{
                  editing: !['PRO #', 'BOL', 'QUOTE #', 'Inv #'].includes(identifier.label) && editingIdentifierInfo,
                  editNode: editIdentifierField(identifier, index),
                }}
              />
            ))}
          </>
        )}
        {identifierInfoLoading && <LoadingContainer><Spinner /></LoadingContainer>}
      </InfoSection>
      <InfoSection numCols={3} header="Interline Carrier Details" headerContent={renderInterlineCarrierHeaderContent()}>
        {!interlineLoading && (
          <>
            {interlineDetails.map((item, index) => (
              <LabelValue
                label={item.label}
                value={item.value}
                options={{ stacked: true }}
                editOptions={{
                  editing: editingInterlineCarrier,
                  editNode: editInterlineField(item, index),
                }}
              />
            ))}
          </>
        )}
        {interlineLoading && <LoadingContainer><Spinner /></LoadingContainer>}
      </InfoSection>
    </Card>
  );
};

LoadIdentifiersCard.propTypes = {
  lock: lockShape,
  load: loadShape,
  refetchLoad: func,
};

LoadIdentifiersCard.defaultProps = {
  lock: null,
  load: null,
  refetchLoad: () => { },
};

export default LoadIdentifiersCard;
